/**
 * Create/Edit Organization Modal Component
 */

import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import {
	useCreateOrganizationMutation,
	useUpdateOrganizationMutation,
	type Organization,
} from "../../../store/api/organizationsApi";

interface CreateOrganizationModalProps {
	isOpen: boolean;
	onClose: () => void;
	organization?: Organization | null;
}

const STATUS_OPTIONS: SelectOption[] = [
	{ value: "active", label: "Active" },
	{ value: "inactive", label: "Inactive" },
	{ value: "suspended", label: "Suspended" },
];

const NIGERIAN_STATES: SelectOption[] = [
	{ value: "Abia", label: "Abia" },
	{ value: "Adamawa", label: "Adamawa" },
	{ value: "Akwa Ibom", label: "Akwa Ibom" },
	{ value: "Anambra", label: "Anambra" },
	{ value: "Bauchi", label: "Bauchi" },
	{ value: "Bayelsa", label: "Bayelsa" },
	{ value: "Benue", label: "Benue" },
	{ value: "Borno", label: "Borno" },
	{ value: "Cross River", label: "Cross River" },
	{ value: "Delta", label: "Delta" },
	{ value: "Ebonyi", label: "Ebonyi" },
	{ value: "Edo", label: "Edo" },
	{ value: "Ekiti", label: "Ekiti" },
	{ value: "Enugu", label: "Enugu" },
	{ value: "FCT", label: "Federal Capital Territory" },
	{ value: "Gombe", label: "Gombe" },
	{ value: "Imo", label: "Imo" },
	{ value: "Jigawa", label: "Jigawa" },
	{ value: "Kaduna", label: "Kaduna" },
	{ value: "Kano", label: "Kano" },
	{ value: "Katsina", label: "Katsina" },
	{ value: "Kebbi", label: "Kebbi" },
	{ value: "Kogi", label: "Kogi" },
	{ value: "Kwara", label: "Kwara" },
	{ value: "Lagos", label: "Lagos" },
	{ value: "Nasarawa", label: "Nasarawa" },
	{ value: "Niger", label: "Niger" },
	{ value: "Ogun", label: "Ogun" },
	{ value: "Ondo", label: "Ondo" },
	{ value: "Osun", label: "Osun" },
	{ value: "Oyo", label: "Oyo" },
	{ value: "Plateau", label: "Plateau" },
	{ value: "Rivers", label: "Rivers" },
	{ value: "Sokoto", label: "Sokoto" },
	{ value: "Taraba", label: "Taraba" },
	{ value: "Yobe", label: "Yobe" },
	{ value: "Zamfara", label: "Zamfara" },
];

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
	isOpen,
	onClose,
	organization,
}) => {
	const [createOrganization, { isLoading: isCreating }] =
		useCreateOrganizationMutation();
	const [updateOrganization, { isLoading: isUpdating }] =
		useUpdateOrganizationMutation();

	const [formData, setFormData] = useState<Partial<Organization>>({
		name: "",
		email: "",
		phone: "",
		address: "",
		city: "",
		state: "",
		zip_code: "",
		country: "Nigeria",
		status: "active",
		bank_name: "",
		account_number: "",
		account_name: "",
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (organization) {
			setFormData({
				name: organization.name || "",
				email: organization.email || "",
				phone: organization.phone || "",
				address: organization.address || "",
				city: organization.city || "",
				state: organization.state || "",
				zip_code: organization.zip_code || "",
				country: organization.country || "Nigeria",
				status: organization.status || "active",
				bank_name: organization.bank_name || "",
				account_number: organization.account_number || "",
				account_name: organization.account_name || "",
			});
		} else {
			setFormData({
				name: "",
				email: "",
				phone: "",
				address: "",
				city: "",
				state: "",
				zip_code: "",
				country: "Nigeria",
				status: "active",
				bank_name: "",
				account_number: "",
				account_name: "",
			});
		}
		setErrors({});
	}, [organization, isOpen]);

	const handleInputChange = (field: keyof Organization, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.name?.trim()) {
			newErrors.name = "Organization name is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		try {
			if (organization) {
				await updateOrganization({
					id: organization.id,
					data: formData,
				}).unwrap();
			} else {
				await createOrganization(formData).unwrap();
			}
			onClose();
		} catch (error: any) {
			console.error("Failed to save organization:", error);
			setErrors({
				submit:
					error?.data?.error ||
					error?.data?.message ||
					"Failed to save organization. Please try again.",
			});
		}
	};

	if (!isOpen) return null;

	const isLoading = isCreating || isUpdating;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-border">
					<h2 className="text-xl font-semibold text-foreground">
						{organization ? "Edit Organization" : "Create Organization"}
					</h2>
					<button
						onClick={onClose}
						disabled={isLoading}
						className="text-muted-foreground hover:text-foreground transition-colors"
					>
						<Icon name="X" size={20} />
					</button>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
					{errors.submit && (
						<div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
							{errors.submit}
						</div>
					)}

					<div className="space-y-6">
						{/* Basic Information */}
						<div>
							<h3 className="text-lg font-medium text-foreground mb-4">
								Basic Information
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<Input
									label="Organization Name *"
									value={formData.name || ""}
									onChange={(e) => handleInputChange("name", e.target.value)}
									error={errors.name}
									required
								/>
								<Input
									label="Email"
									type="email"
									value={formData.email || ""}
									onChange={(e) => handleInputChange("email", e.target.value)}
								/>
								<Input
									label="Phone"
									value={formData.phone || ""}
									onChange={(e) => handleInputChange("phone", e.target.value)}
								/>
								<Select
									label="Status"
									options={STATUS_OPTIONS}
									value={formData.status || "active"}
									onValueChange={(value) =>
										handleInputChange("status", String(value))
									}
								/>
							</div>
						</div>

						{/* Address */}
						<div>
							<h3 className="text-lg font-medium text-foreground mb-4">
								Address
							</h3>
							<div className="space-y-4">
								<Input
									label="Street Address"
									value={formData.address || ""}
									onChange={(e) => handleInputChange("address", e.target.value)}
								/>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<Input
										label="City"
										value={formData.city || ""}
										onChange={(e) => handleInputChange("city", e.target.value)}
									/>
									<div>
										<label className="mb-2 block text-sm font-medium text-foreground">
											State
										</label>
										<Select
											value={formData.state || ""}
											options={[
												{ value: "", label: "Select a state" },
												...NIGERIAN_STATES,
											]}
											onValueChange={(value) =>
												handleInputChange("state", value as string)
											}
										/>
									</div>
									<Input
										label="Zip Code"
										value={formData.zip_code || ""}
										onChange={(e) =>
											handleInputChange("zip_code", e.target.value)
										}
									/>
								</div>
								<Input
									label="Country"
									value={formData.country || "Nigeria"}
									onChange={(e) => handleInputChange("country", e.target.value)}
								/>
							</div>
						</div>

						{/* Bank Details */}
						<div>
							<h3 className="text-lg font-medium text-foreground mb-4">
								Bank Details
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<Input
									label="Bank Name"
									value={formData.bank_name || ""}
									onChange={(e) =>
										handleInputChange("bank_name", e.target.value)
									}
								/>
								<Input
									label="Account Number"
									value={formData.account_number || ""}
									onChange={(e) =>
										handleInputChange("account_number", e.target.value)
									}
								/>
								<Input
									label="Account Name"
									value={formData.account_name || ""}
									onChange={(e) =>
										handleInputChange("account_name", e.target.value)
									}
									className="md:col-span-2"
								/>
							</div>
						</div>
					</div>
				</form>

				{/* Footer */}
				<div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
					<Button variant="outline" onClick={onClose} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						type="submit"
						onClick={handleSubmit}
						disabled={isLoading}
						iconName={isLoading ? "Loader2" : organization ? "Save" : "Plus"}
						iconPosition="left"
					>
						{isLoading
							? organization
								? "Updating..."
								: "Creating..."
							: organization
							? "Update Organization"
							: "Create Organization"}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default CreateOrganizationModal;
